



































































































import {
  Component, Ref, Vue, Watch,
} from 'vue-property-decorator';
import {
  filter, flatMap, isFinite, map, sum,
} from 'lodash';
import fuzzysort from 'fuzzysort';
import { Message } from 'element-ui';
import BankAccountsModule from '@/store/bank-accounts';
import PaymentOrdersModule from '@/store/payment-orders';

import PaymentOrderGroup from '@/components/PaymentOrderGroup.vue';
import PaymentOrderHeader from '@/components/PaymentOrderHeader.vue';

import ChooseDebtorModal from '@/components/ChooseDebtorModal.vue';
import CreditorModal from '@/components/TransactionParticipantModal.vue';
import ScanField from '@/components/ScanField.vue';
import TransactionDto from '@/interfaces/transaction.dto';
import Deferred from '@/utils/deferred';
import { formatMoney } from '@/utils/format';
import { BankAccount, Transaction, TransactionGroup } from '@/api/payment-orders';
import ChoosePartyModal from '@/components/ChoosePartyModal.vue';
import PartyModal from '@/components/PartyModal.vue';

@Component({
  name: 'PaymentOrder',
  components: {
    PartyModal,
    ChoosePartyModal,
    ScanField,
    PaymentOrderHeader,
    PaymentOrderGroup,
    ChooseDebtorModal,
    CreditorModal,
  },
})
export default class PaymentOrder extends Vue {
  activeGroup = '';

  search = '';

  // Variables used for tracking new creditor submit initiated by transaction component
  isCreditorModalOpen = false;

  creditor: Partial<BankAccount> | null = null;

  debtor: Partial<BankAccount> | null = null;

  // Variables used for tracking new debtor submit

  isDebtorModalOpen = false;

  isScanMode = true;

  isScanModeBeforeModalOpen = false;

  get paymentOrder() {
    return PaymentOrdersModule.paymentOrder;
  }

  get parties(): BankAccount[] | null {
    return BankAccountsModule.bankAccounts;
  }

  get debtors(): BankAccount[] | null {
    return filter(this.parties, 'isPayable');
  }

  get creditors(): BankAccount[] | null {
    return this.parties;
  }

  @Ref('scanField')
  readonly scanField!: ScanField;

  get initiatingPartyName() {
    return this.paymentOrder.initiatingPartyName;
  }

  get groups() {
    return this.paymentOrder.transactionGroups;
  }

  get groupsSorted() {
    return this.groups.sort((a, b) => a.debtor.name.localeCompare(b.debtor.name));
  }

  get groupsSortedAndFiltered() {
    if (!this.search) {
      return this.groupsSorted;
    }

    return map(fuzzysort.go(this.search, this.groupsSorted,
                            { key: 'debtor.name', allowTypo: false }), 'obj');
  }

  get numberOfTransactions() {
    return sum(map(this.groups, 'transactions.length'));
  }

  get controlSum() {
    const transactions = flatMap(this.groups, 'transactions');

    return this.calculateControlSum(transactions);
  }

  get isAnyModalOpen() {
    return this.isDebtorModalOpen || this.isCreditorModalOpen;
  }

  @Watch('isAnyModalOpen')
  async onIsAnyModalOpenChange(newValue: boolean) {
    if (newValue) {
      this.isScanModeBeforeModalOpen = this.isScanMode;
    } else if (this.isScanModeBeforeModalOpen) {
      window.setTimeout(() => {
        this.isScanMode = true;
        this.scanField.focusScanArea();
      }, 1000);
    }
  }

  calculateControlSum(transactions: Transaction[]) {
    return sum(filter(map(transactions, 'amount'), isFinite));
  }

  displayControlSum(transactions: Transaction[]) {
    const controlSum = this.calculateControlSum(transactions);

    if (!controlSum) {
      return '';
    }

    return `-${formatMoney(controlSum)} EUR`;
  }

  creditorDeferred: Deferred<BankAccount> | null = null;

  debtorDeferred: Deferred<BankAccount> | null = null;

  async onAddGroupClick() {
    const debtor = await this.chooseDebtor();

    if (!debtor) {
      Message.error('Platitelj nije odabran');
      return;
    }

    this.addGroup(debtor);
  }

  onGroupDelete(groupId: string) {
    this.paymentOrder.transactionGroups = this.groups.filter(({ id }) => id !== groupId);
  }

  onDebtorSelect(debtor: BankAccount) {
    console.log({ debtor });
    // eslint-disable-next-line no-unused-expressions
    this.debtorDeferred?.resolve(debtor);

    this.closeDebtorModal();
  }

  onCreditorSelect(creditor: BankAccount) {
    // eslint-disable-next-line no-unused-expressions
    this.creditorDeferred?.resolve(creditor);

    this.closeCreditorModal();
  }

  async onElementAdd(groupId: string) {
    this.activeGroup = groupId;
    await this.$nextTick();

    const elRef = this.$refs[groupId];

    if ('length' in elRef && '$el' in elRef[0]) {
      elRef[0].$el.scrollIntoView(false);
    }
  }

  addGroup(debtor: BankAccount) {
    const group = TransactionGroup.from(debtor);

    this.groups.push(group);

    this.activeGroup = group.id;

    this.onElementAdd(group.id);

    return group;
  }

  openCreditorModal() {
    this.isCreditorModalOpen = true;
  }

  closeCreditorModal() {
    this.isCreditorModalOpen = false;
  }

  openDebtorModal() {
    this.isDebtorModalOpen = true;
  }

  closeDebtorModal() {
    this.isDebtorModalOpen = false;
  }

  async chooseDebtor(debtorInitial: Partial<BankAccount> | null = null) {
    this.debtor = debtorInitial;

    this.openDebtorModal();

    this.debtorDeferred = new Deferred<BankAccount>();

    const debtor = await this.debtorDeferred;

    this.debtorDeferred = null;

    return debtor;
  }

  async chooseCreditor(creditorInitial: Partial<BankAccount> | null = null) {
    this.creditor = creditorInitial;

    this.openCreditorModal();

    this.creditorDeferred = new Deferred<BankAccount>();

    const creditor = await this.creditorDeferred;

    this.creditorDeferred = null;

    return creditor;
  }

  async onTransactionScan(transactionDto: TransactionDto | null) {
    if (!transactionDto) {
      Message.error('Skeniranje neuspješno!');

      return;
    }

    const {
      crdtrNm, crdtrIban, amount, info, model, ref, dbtrAddr,
    } = transactionDto;

    let creditor = this.creditors?.find(
      ({ iban }) => iban === crdtrIban,
    );

    if (!creditor) {
      creditor = await this.chooseCreditor(
        {
          name: crdtrNm,
          iban: crdtrIban,
          isPayable: false,
        },
      );
    }

    if (!creditor) {
      Message.error('Primatelj nije odabran');
      return;
    }

    const transaction = Transaction.from(creditor, amount, {
      model,
      beneficiaryRef: ref,
      additionalInfo: info,
      isScanned: true,
    });

    let debtor = this.debtors?.find(({ name }) => name === transactionDto.dbtrAddr);

    if (!debtor) {
      debtor = await this.chooseDebtor({ name: dbtrAddr, isPayable: true });
    } else if (!debtor.iban) {
      debtor = await this.chooseDebtor(debtor);
    }

    if (!debtor) {
      Message.error('Platitelj nije odabran');
      return;
    }

    let group = this.groups.find((grp) => grp.debtor.iban === debtor?.iban);

    if (!group) {
      group = this.addGroup(debtor);
    }

    group.transactions.push(transaction);

    Message.success('Skeniranje uspješno!');

    this.onElementAdd(group.id);
  }

  async onPaymentOrderGenerate() {
    try {
      await PaymentOrdersModule.generatePaymentOrder(this.paymentOrder);
    } catch (e) {
      if (e instanceof Error) {
        Message.error(e.message);
      }
    }
  }
}
